import { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { BadgePlus } from "lucide-react";
import axios from "axios";
import DeleteConfirmation from "../../../components/DeleteConfirmation";
import { createDeepinfraClonedVoiceAPI, fetchDeepinfraClonedVoicesAPI, fetchMeUser, fetchStaffUsersAPI, saveStaffVoicesAPI } from "../../../services/user";
import { getAccessToken } from "../../../services/token";

// ----- Reusable Components -----
const NoDataFound = () => (
  <div className="flex flex-col items-center justify-center h-full gap-2 py-6">
    <div className="w-10 h-10 rounded-full border-2 border-blue-200 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </div>
    <span className="text-gray-400 text-sm font-medium">No Data Found</span>
  </div>
);

const aiModels = [
  { name: "Kokoro-82M (Standard)", model: "hexgrad/Kokoro-82M" },
  { name: "Orpheus-3B (Standard)", model: "canopylabs/orpheus-3b-0.1-ft" },
  { name: "MiMo-V2.5-tts-voiceclone (Clone)", model: "XiaomiMiMo/MiMo-V2.5-tts-voiceclone" },
  { name: "MiMo-V2.5-tts-voicedesign (Design)", model: "XiaomiMiMo/MiMo-V2.5-tts-voicedesign" },
];

// ----- Static background sounds (with proper name for "None") -----
const RANDOM_BACKGROUND_URL = "__random__";
const CUSTOM_BACKGROUND_VALUE = "__custom_background__";
const CLONE_VOICE_UPLOAD_VALUE = "__clone_voice_upload__";
const CLONED_VOICE_PREFIX = "deepinfra_clone:";
const DEMO_SPEECH_URL = `https://ai-portal.webuyhouses.icu/audio/demo_speech.mp3`;

const backgroundSounds = [
  { name: "None", url: null },
  { name: "Ambiance", url: "https://ai-portal.webuyhouses.icu/sounds/ambiance.mp3" },
  { name: "Bank Ambience", url: "https://ai-portal.webuyhouses.icu/sounds/bank_ambience.mp3" },
  { name: "City Crowd", url: "https://ai-portal.webuyhouses.icu/sounds/city_crowd.mp3" },
  { name: "Library Ambience", url: "https://ai-portal.webuyhouses.icu/sounds/library_ambience.mp3" },
  { name: "NYC Street", url: "https://ai-portal.webuyhouses.icu/sounds/nyc_street.mp3" },
  { name: "Office Ambience 1", url: "https://ai-portal.webuyhouses.icu/sounds/office_ambience_1.mp3" },
  { name: "Office Ambience 2", url: "https://ai-portal.webuyhouses.icu/sounds/office_ambience_2.mp3" },
  { name: "People in Lounge", url: "https://ai-portal.webuyhouses.icu/sounds/people-in-lounge-1.mp3" },
  { name: "Random Background Noise", url: RANDOM_BACKGROUND_URL },
];

const voicemailTypes = [
  { key: "static_text", label: "Static Text" },
  { key: "static_audio", label: "Static Audio" },
  { key: "dynamic_text", label: "Dynamic Text" },
  { key: "ghl_field", label: "GHL Field" },
];

const getVoicemailTypeLabel = (type) => voicemailTypes.find((item) => item.key === type)?.label || "Static Text";

// ----- Main Component -----
export default function VoicemailDashboard() {
  const [loading, setLoading] = useState(false);
  const [voicemails, setVoicemails] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(25);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [context, setContext] = useState("");
  const [voicemailType, setVoicemailType] = useState("static_text");
  const [staticAudioFile, setStaticAudioFile] = useState(null);
  const [ghlFields, setGhlFields] = useState([]);
  const [ghlFieldsLoading, setGhlFieldsLoading] = useState(false);
  const [selectedGhlField, setSelectedGhlField] = useState("");
  const [selectedBgSound, setSelectedBgSound] = useState(backgroundSounds[0].url); // default: None
  const [generating, setGenerating] = useState(false);
  const [staffVoiceModalOpen, setStaffVoiceModalOpen] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffVoiceLoading, setStaffVoiceLoading] = useState(false);
  const [staffVoiceSaving, setStaffVoiceSaving] = useState(false);
  const [clonedVoices, setClonedVoices] = useState([]);
  const [cloneVoiceForms, setCloneVoiceForms] = useState({});
  const [cloneVoiceUploading, setCloneVoiceUploading] = useState(null);

  // ---- NEW: background mixing parameters ----
  const [bgVolume, setBgVolume] = useState(0.15); // 0.0 - 1.0
  const [voiceVolume, setVoiceVolume] = useState(1.0); // 0.0 - 1.0

  const [customBackgrounds, setCustomBackgrounds] = useState([]);
  const [customBgName, setCustomBgName] = useState("");
  const [customBgFile, setCustomBgFile] = useState(null);
  const [customBgUploading, setCustomBgUploading] = useState(false);

  // Preview player states
  const [isPlaying, setIsPlaying] = useState(false);
  const bgAudioRef = useRef(null);
  const speechAudioRef = useRef(null);

  // Delete states
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedVoicemailId, setSelectedVoicemailId] = useState(null);

  // Detail modal (View)
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedVoicemail, setSelectedVoicemail] = useState(null);

  const allBackgroundSounds = [
    ...backgroundSounds,
    ...customBackgrounds.map((item) => ({
      name: item.name,
      url: item.audio_url,
    })),
  ];
  const previewableBackgroundSounds = allBackgroundSounds.filter((sound) => sound.url && sound.url !== RANDOM_BACKGROUND_URL && sound.url !== CUSTOM_BACKGROUND_VALUE);

  // Fetch voicemails
  const fetchVoicemails = async (newOffset = offset) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails`, {
        params: { limit, offset: newOffset },
      });
      if (data?.success) {
        setVoicemails(data.data.data || []);
        setTotal(data.data.total || 0);
        setOffset(data.data.offset || 0);
      }
    } catch (error) {
      console.error("Error fetching voicemails:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomBackgrounds = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const { data } = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails/background-noises`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomBackgrounds(data?.success ? data.data || [] : []);
    } catch (error) {
      console.error("Error fetching custom background noises:", error);
    }
  };

  useEffect(() => {
    fetchVoicemails(0);
    fetchCustomBackgrounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pagination
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  const goToPage = (page) => {
    const newOffset = (page - 1) * limit;
    if (newOffset !== offset) fetchVoicemails(newOffset);
  };

  const handlePrevious = () => {
    if (currentPage > 1) goToPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) goToPage(currentPage + 1);
  };

  const resetVoicemailModal = () => {
    setModalOpen(false);
    setContext("");
    setVoicemailType("static_text");
    setStaticAudioFile(null);
    setSelectedGhlField("");
    setSelectedBgSound(backgroundSounds[0].url);
    setCustomBgName("");
    setCustomBgFile(null);
    setBgVolume(0.15);
    setVoiceVolume(1.0);
    stopPreview();
  };

  const fetchGhlFields = async () => {
    try {
      setGhlFieldsLoading(true);
      const userResponse = await fetchMeUser();
      const locationId = String(userResponse?.data?.locationId || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)[0];
      const { data } = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails/ghl-fields`, {
        params: locationId ? { locationId } : {},
      });
      setGhlFields(data?.success ? data.data || [] : []);
    } catch (error) {
      console.error("Error fetching GHL fields:", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to fetch GHL fields.");
    } finally {
      setGhlFieldsLoading(false);
    }
  };

  useEffect(() => {
    if (modalOpen && voicemailType === "ghl_field" && !ghlFields.length) {
      fetchGhlFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, voicemailType]);

  const isGenerateDisabled = () => {
    if (generating) return true;
    if (voicemailType !== "static_audio" && selectedBgSound === CUSTOM_BACKGROUND_VALUE) return true;
    if (voicemailType === "static_audio") return !staticAudioFile;
    if (voicemailType === "ghl_field") return !selectedGhlField;
    return !context.trim();
  };

  // Create new voicemail
  const handleGenerate = async () => {
    if (voicemailType === "static_audio" && !staticAudioFile) {
      toast.warning("Please upload an mp3 or wav file.");
      return;
    }

    if ((voicemailType === "static_text" || voicemailType === "dynamic_text") && !context.trim()) {
      toast.warning("Please enter voicemail content.");
      return;
    }

    if (voicemailType === "ghl_field" && !selectedGhlField) {
      toast.warning("Please select a GHL field.");
      return;
    }

    if (selectedBgSound === CUSTOM_BACKGROUND_VALUE) {
      toast.warning("Upload the custom background first, then select it from the dropdown.");
      return;
    }

    try {
      setGenerating(true);
      const selectedSound = allBackgroundSounds.find((s) => s.url === selectedBgSound);
      const bgName = selectedSound ? selectedSound.name : "None";
      let payload;

      if (voicemailType === "static_audio") {
        payload = new FormData();
        payload.append("voicemail_type", voicemailType);
        payload.append("audio_file", staticAudioFile);
        payload.append("content", staticAudioFile.name);
      } else {
        payload = {
          voicemail_type: voicemailType,
          content: voicemailType === "ghl_field" ? selectedGhlField : context,
          ghl_field_key: voicemailType === "ghl_field" ? selectedGhlField : undefined,
          bg_url: selectedBgSound,
          bg_name: bgName,
        };

        if (selectedBgSound) {
          payload.bg_volume = bgVolume;
          payload.voice_volume = voiceVolume;
        }
      }

      const { data } = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails`, payload);
      if (data?.success) {
        resetVoicemailModal();
        fetchVoicemails(0);
        toast.success("Voicemail saved successfully.");
      } else {
        toast.error(data?.message || "Failed to save voicemail.");
      }
    } catch (error) {
      console.error("Error generating voicemail:", error);
      toast.error(error?.response?.data?.message || error?.message || "Error adding voicemail.");
    } finally {
      setGenerating(false);
    }
  };

  const handleUploadCustomBackground = async () => {
    if (!customBgName.trim()) {
      toast.warning("Please enter a custom background name.");
      return;
    }

    if (!customBgFile) {
      toast.warning("Please upload an mp3 or wav file.");
      return;
    }

    try {
      setCustomBgUploading(true);
      const token = await getAccessToken();
      const payload = new FormData();
      payload.append("name", customBgName.trim());
      payload.append("audio_file", customBgFile);

      const { data } = await axios.post(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails/background-noises`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data?.success && data.data) {
        setCustomBackgrounds((current) => [data.data, ...current]);
        setSelectedBgSound(data.data.audio_url);
        setCustomBgName("");
        setCustomBgFile(null);
        toast.success("Custom background noise uploaded successfully.");
      } else {
        toast.error(data?.message || "Failed to upload custom background noise.");
      }
    } catch (error) {
      console.error("Error uploading custom background noise:", error);
      toast.error(error?.response?.data?.message || error?.message || "Failed to upload custom background noise.");
    } finally {
      setCustomBgUploading(false);
    }
  };

  const fetchStaffVoices = async () => {
    try {
      setStaffVoiceLoading(true);
      const [staffResponseData, clonedVoiceResponseData] = await Promise.all([fetchStaffUsersAPI(), fetchDeepinfraClonedVoicesAPI()]);
      if (staffResponseData && staffResponseData.status === "success") {
        setStaffUsers(staffResponseData.data || []);
      }
      if (clonedVoiceResponseData && clonedVoiceResponseData.status === "success") {
        setClonedVoices(clonedVoiceResponseData.data || []);
      }
    } catch (error) {
      console.error("Error fetching staff voices:", error);
      toast.error(error?.message || "Failed to fetch staff users.");
    } finally {
      setStaffVoiceLoading(false);
    }
  };

  const openStaffVoiceModal = () => {
    setCloneVoiceForms({});
    setStaffVoiceModalOpen(true);
    fetchStaffVoices();
  };

  const handleStaffVoiceChange = (staffUserId, voicemailVoice) => {
    setStaffUsers((currentStaffUsers) => currentStaffUsers.map((staffUser) => (staffUser.id === staffUserId ? { ...staffUser, voicemail_voice: voicemailVoice } : staffUser)));
  };

  const handleCloneVoiceFormChange = (staffUserId, patch) => {
    setCloneVoiceForms((currentForms) => ({
      ...currentForms,
      [staffUserId]: {
        ...(currentForms[staffUserId] || {}),
        ...patch,
      },
    }));
  };

  const handleUploadCloneVoice = async (staffUserId) => {
    const form = cloneVoiceForms[staffUserId] || {};

    if (!form.name || !form.name.trim()) {
      toast.warning("Please enter a voice name.");
      return;
    }

    if (!form.file) {
      toast.warning("Please upload an mp3 or wav file.");
      return;
    }

    try {
      setCloneVoiceUploading(staffUserId);
      const payload = new FormData();
      payload.append("name", form.name.trim());
      payload.append("audio_file", form.file);

      const responseData = await createDeepinfraClonedVoiceAPI(payload);
      if (responseData && responseData.status === "success" && responseData.data) {
        setClonedVoices((currentVoices) => [responseData.data, ...currentVoices]);
        setStaffUsers((currentStaffUsers) => currentStaffUsers.map((staffUser) => (staffUser.id === staffUserId ? { ...staffUser, voicemail_voice: `${CLONED_VOICE_PREFIX}${responseData.data.voice_id}` } : staffUser)));
        setCloneVoiceForms((currentForms) => ({
          ...currentForms,
          [staffUserId]: {},
        }));
        toast.success("Clone voice uploaded successfully.");
      }
    } catch (error) {
      console.error("Error uploading clone voice:", error);
      toast.error(error?.message || "Failed to upload clone voice.");
    } finally {
      setCloneVoiceUploading(null);
    }
  };

  const handleSaveStaffVoices = async () => {
    if (staffUsers.some((staffUser) => staffUser.voicemail_voice === CLONE_VOICE_UPLOAD_VALUE)) {
      toast.warning("Upload the clone voice first, then save staff voices.");
      return;
    }

    try {
      setStaffVoiceSaving(true);
      const responseData = await saveStaffVoicesAPI({
        voices: staffUsers.map((staffUser) => ({
          staffUserId: staffUser.id,
          voicemail_voice: staffUser.voicemail_voice || null,
        })),
      });

      if (responseData && responseData.status === "success") {
        toast.success("Staff voices saved successfully.");
        setStaffVoiceModalOpen(false);
      }
    } catch (error) {
      console.error("Error saving staff voices:", error);
      toast.error(error?.message || "Failed to save staff voices.");
    } finally {
      setStaffVoiceSaving(false);
    }
  };

  // Delete voicemail
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_BASE_URL}/api/webhook/voicemails/${id}`);
      toast.success("Voicemail deleted successfully.");
      fetchVoicemails(offset);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete voicemail.");
    } finally {
      setIsDeleteOpen(false);
      setSelectedVoicemailId(null);
    }
  };

  const toggleDeletePopup = (id) => {
    setSelectedVoicemailId(id);
    setIsDeleteOpen(!isDeleteOpen);
  };

  // View details
  const handleView = (vm) => {
    setSelectedVoicemail(vm);
    setDetailModalOpen(true);
  };

  // ---- Background + speech preview handlers ----
  const stopPreview = () => {
    [bgAudioRef.current, speechAudioRef.current].forEach((audio) => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    });
    setIsPlaying(false);
  };

  const getPreviewBackgroundSound = () => {
    if (selectedBgSound === RANDOM_BACKGROUND_URL) {
      if (!previewableBackgroundSounds.length) return null;
      return previewableBackgroundSounds[Math.floor(Math.random() * previewableBackgroundSounds.length)];
    }

    return allBackgroundSounds.find((sound) => sound.url === selectedBgSound) || null;
  };

  const togglePlayPreview = () => {
    if (isPlaying) {
      stopPreview();
      return;
    }

    if (voicemailType === "static_audio") {
      return;
    }

    if (selectedBgSound === CUSTOM_BACKGROUND_VALUE) {
      toast.warning("Upload the custom background first, then select it from the dropdown.");
      return;
    }

    if (!speechAudioRef.current) return;

    const backgroundSound = getPreviewBackgroundSound();
    const playTasks = [];

    speechAudioRef.current.src = DEMO_SPEECH_URL;
    speechAudioRef.current.volume = Number(voiceVolume);
    speechAudioRef.current.currentTime = 0;
    speechAudioRef.current.load();

    if (backgroundSound?.url && bgAudioRef.current) {
      bgAudioRef.current.src = backgroundSound.url;
      bgAudioRef.current.volume = Number(bgVolume);
      bgAudioRef.current.currentTime = 0;
      bgAudioRef.current.load();
      playTasks.push(bgAudioRef.current.play());
    }

    playTasks.push(speechAudioRef.current.play());

    Promise.all(playTasks)
      .then(() => setIsPlaying(true))
      .catch((err) => {
        console.error("Preview play failed:", err);
        stopPreview();
        toast.warning("Could not play preview. Check the selected sound URL.");
      });
  };

  // When selectedBgSound changes, reset playing state
  useEffect(() => {
    stopPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBgSound]);

  useEffect(() => {
    if (bgAudioRef.current) bgAudioRef.current.volume = Number(bgVolume);
    if (speechAudioRef.current) speechAudioRef.current.volume = Number(voiceVolume);
  }, [bgVolume, voiceVolume]);

  const formatDate = (isoString) => {
    if (!isoString) return "—";
    return new Date(isoString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Helper to get background sound name from URL (for display in list/detail)
  const getBackgroundSoundName = (url) => {
    if (!url) return "None";
    if (url === RANDOM_BACKGROUND_URL) return "Random Background Noise";
    const found = allBackgroundSounds.find((s) => s.url === url);
    return found ? found.name : url;
  };

  return (
    <div>
      {loading ? (
        <div className="flex justify-center mt-10">
          <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-10 w-10 opacity-90"></div>
        </div>
      ) : (
        <div>
          {/* Header with Add button */}
          <div className="w-full flex justify-end">
            <div className="py-5 flex gap-3 px-5">
              <button className="text-white bg-gray-700 hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2 dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-gray-800" type="button" onClick={openStaffVoiceModal}>
                Staff Voices
              </button>
              <button className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" type="button" onClick={() => setModalOpen(true)}>
                <BadgePlus className="inline mb-1 mr-1" size="16" />
                Add Voicemail
              </button>
            </div>
          </div>

          {voicemails && voicemails.length > 0 ? (
            <>
              <div className="px-5">
                <table className="min-w-full divide-y bg-back-dark divide-color-lines rounded-lg">
                  <thead className="min-w-full rounded-md divide-color-lines">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        ID
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Type
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Content
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Background
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        File Name
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Audio
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Created At
                      </th>
                      <th scope="col" className="px-4 py-2 text-center text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-back-dark divide-y divide-color-lines">
                    {voicemails.map((vm) => (
                      <tr key={vm.id} className="hover:bg-gray-900 text-xs">
                        <td className="px-4 py-2 whitespace-wrap">{vm.id}</td>
                        <td className="px-4 py-2 whitespace-wrap">{getVoicemailTypeLabel(vm.voicemail_type)}</td>
                        <td className="px-4 py-2 whitespace-wrap max-w-xs truncate" title={vm.content}>
                          {vm.content}
                        </td>
                        <td className="px-4 py-2 whitespace-wrap">{getBackgroundSoundName(vm.bg_url || vm.background_sound_url)}</td>
                        <td className="px-4 py-2 whitespace-wrap">{vm.file_name || "—"}</td>
                        <td className="px-4 py-2 whitespace-wrap">{vm.audio_url ? <audio controls src={vm.audio_url} className="w-32 h-6" /> : "N/A"}</td>
                        <td className="px-4 py-2 whitespace-wrap">{formatDate(vm.created_at)}</td>
                        <td className="px-4 py-2 whitespace-wrap flex gap-4 items-center">
                          <button className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2 dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 mr-2" onClick={() => handleView(vm)}>
                            View
                          </button>
                          <button className="text-white bg-red-700 hover:bg-red-800 focus:ring-4 font-medium rounded-lg text-sm px-3 py-2 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-800" onClick={() => toggleDeletePopup(vm.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination controls */}
              {total > 0 && (
                <div className="flex items-center justify-between mt-4 text-xs text-gray-400 px-5 py-2">
                  <span>
                    Showing {offset + 1} – {Math.min(offset + limit, total)} of {total}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={handlePrevious} disabled={currentPage === 1} className={`px-3 py-1 rounded-md border ${currentPage === 1 ? "bg-gray-700 text-gray-500 cursor-not-allowed" : "bg-gray-800 text-gray-300 hover:bg-gray-700"} transition-colors`}>
                      Previous
                    </button>
                    <span className="px-3 py-1 bg-blue-900 text-blue-300 rounded-md">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button onClick={handleNext} disabled={currentPage === totalPages} className={`px-3 py-1 rounded-md border ${currentPage === totalPages ? "bg-gray-700 text-gray-500 cursor-not-allowed" : "bg-gray-800 text-gray-300 hover:bg-gray-700"} transition-colors`}>
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center text-center text-xl pt-10">
              <div>
                No Voicemails found.
                <div className="block text-base mt-5 mb-10">Start by creating a new voicemail.</div>
                <div className="flex items-center justify-center">
                  <div onClick={() => setModalOpen(true)} className="rounded-lg text-center overflow-hidden bg-blue-700 p-4 hover:bg-blue-900 cursor-pointer">
                    <BadgePlus className="inline mb-2" size="30" />
                    <p className="text-base">Create New Voicemail</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ----- Add Voicemail Modal ----- */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-100">Generate New Voicemail</h2>
              <button onClick={resetVoicemailModal} className="text-gray-400 hover:text-gray-200">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {voicemailTypes.map((type) => (
                  <button key={type.key} type="button" onClick={() => setVoicemailType(type.key)} className={`px-3 py-2 text-sm rounded-lg border transition-colors ${voicemailType === type.key ? "bg-blue-600 border-blue-500 text-white" : "bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600"}`}>
                    {type.label}
                  </button>
                ))}
              </div>

              {voicemailType === "static_text" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Static Text</label>
                  <textarea rows={4} className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Enter the text to be used every time..." value={context} onChange={(e) => setContext(e.target.value)} />
                </div>
              )}

              {voicemailType === "dynamic_text" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Dynamic Text</label>
                  <textarea rows={4} className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Example: Hi {{contact.first_name}}, calling about {{contact.property_address}}..." value={context} onChange={(e) => setContext(e.target.value)} />
                  <p className="text-xs text-gray-400 mt-1">Use contact placeholders such as {"{{contact.first_name}}"}. Values are replaced when the voicemail is dropped.</p>
                </div>
              )}

              {voicemailType === "static_audio" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Static Audio</label>
                  <input type="file" accept=".mp3,.wav,audio/mpeg,audio/wav,audio/wave,audio/x-wav" className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => setStaticAudioFile(e.target.files?.[0] || null)} />
                  <p className="text-xs text-gray-400 mt-1">This audio file is played directly in the Twilio call. Staff voice and background audio are not used.</p>
                </div>
              )}

              {voicemailType === "ghl_field" && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">GHL Field</label>
                  <select className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedGhlField} onChange={(e) => setSelectedGhlField(e.target.value)} disabled={ghlFieldsLoading}>
                    <option value="">{ghlFieldsLoading ? "Loading fields..." : "Select GHL field"}</option>
                    {ghlFields.map((field) => (
                      <option key={field.fieldKey} value={field.fieldKey}>
                        {field.groupLabel ? `${field.groupLabel} / ` : ""}
                        {field.fieldLabel || field.fieldKey}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-400 mt-1">The selected field value from the contact is converted to speech when the voicemail is dropped.</p>
                </div>
              )}

              {voicemailType !== "static_audio" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">Background Sound</label>
                    <select className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedBgSound || ""} onChange={(e) => setSelectedBgSound(e.target.value || null)}>
                      {allBackgroundSounds.map((sound) => (
                        <option key={`${sound.name}-${sound.url || "none"}`} value={sound.url || ""}>
                          {sound.name}
                        </option>
                      ))}
                      <option value={CUSTOM_BACKGROUND_VALUE}>Custom Background Noise</option>
                    </select>
                    <audio ref={bgAudioRef} className="hidden" />
                    <audio ref={speechAudioRef} onEnded={stopPreview} onPause={() => setIsPlaying(false)} className="hidden" />
                    <p className="text-xs text-gray-400 mt-1">Preview plays the selected background together with demo_speech.mp3 using the selected volumes.</p>
                  </div>

                  {selectedBgSound === CUSTOM_BACKGROUND_VALUE && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 border border-gray-700 rounded-lg p-3">
                      <input type="text" className="border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Custom background name" value={customBgName} onChange={(e) => setCustomBgName(e.target.value)} />
                      <input type="file" accept=".mp3,.wav,audio/mpeg,audio/wav,audio/wave,audio/x-wav" className="border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => setCustomBgFile(e.target.files?.[0] || null)} />
                      <button type="button" onClick={handleUploadCustomBackground} disabled={customBgUploading} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${customBgUploading ? "bg-gray-500 cursor-not-allowed text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}>
                        {customBgUploading ? "Uploading..." : "Upload"}
                      </button>
                    </div>
                  )}

                  {selectedBgSound && selectedBgSound !== CUSTOM_BACKGROUND_VALUE && (
                    <div className="space-y-3 border-t border-gray-700 pt-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-300">
                          Background Volume: <span className="text-blue-400">{bgVolume.toFixed(2)}</span>
                        </label>
                        <input type="range" min="0" max="1" step="0.01" value={bgVolume} onChange={(e) => setBgVolume(parseFloat(e.target.value))} className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
                        <p className="text-xs text-gray-400 mt-0.5">0 = silent, 1 = full volume (recommended 0.1–0.3)</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300">
                          Voice Volume: <span className="text-blue-400">{voiceVolume.toFixed(2)}</span>
                        </label>
                        <input type="range" min="0" max="1" step="0.01" value={voiceVolume} onChange={(e) => setVoiceVolume(parseFloat(e.target.value))} className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
                        <p className="text-xs text-gray-400 mt-0.5">Adjust voice loudness (1.0 = normal)</p>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button onClick={resetVoicemailModal} className="px-4 py-2 text-sm rounded-md border border-gray-600 hover:bg-gray-700 transition-colors text-gray-300">
                  Cancel
                </button>
                <button type="button" onClick={togglePlayPreview} disabled={voicemailType === "static_audio" || selectedBgSound === CUSTOM_BACKGROUND_VALUE || customBgUploading} className={`px-4 py-2 text-sm rounded-md text-white ${voicemailType === "static_audio" || selectedBgSound === CUSTOM_BACKGROUND_VALUE || customBgUploading ? "bg-gray-500 cursor-not-allowed" : "bg-gray-600 hover:bg-gray-700"} transition-colors`}>
                  {isPlaying ? "Stop Preview" : "Preview"}
                </button>
                <button onClick={handleGenerate} disabled={isGenerateDisabled()} className={`px-4 py-2 text-sm rounded-md text-white ${isGenerateDisabled() ? "bg-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"} transition-colors`}>
                  {generating ? "Saving..." : "Save Voicemail"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ----- Staff Voices Modal ----- */}
      {staffVoiceModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-100">Staff Voices</h2>
              <button onClick={() => setStaffVoiceModalOpen(false)} className="text-gray-400 hover:text-gray-200">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {staffVoiceLoading ? (
              <div className="flex justify-center mt-10 mb-10">
                <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-10 w-10 opacity-90"></div>
              </div>
            ) : staffUsers && staffUsers.length > 0 ? (
              <div className="max-h-96 overflow-auto">
                <table className="min-w-full divide-y bg-back-dark divide-color-lines rounded-lg">
                  <thead className="min-w-full rounded-md divide-color-lines">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-left text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Staff User
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Email
                      </th>
                      <th scope="col" className="px-4 py-2 text-left text-sm text-slate-400 font-medium tracking-wider whitespace-nowrap">
                        Voice
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-back-dark divide-y divide-color-lines">
                    {staffUsers.map((staffUser) => (
                      <tr key={staffUser.id} className="hover:bg-gray-900 text-sm">
                        <td className="px-4 py-2 whitespace-wrap">{staffUser.staff_name || "—"}</td>
                        <td className="px-4 py-2 whitespace-wrap">{staffUser.staff_email || "—"}</td>
                        <td className="px-4 py-2 whitespace-wrap">
                          <select className="w-full border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" value={staffUser.voicemail_voice || ""} onChange={(e) => handleStaffVoiceChange(staffUser.id, e.target.value)}>
                            <option value="">Select voice</option>
                            {aiModels.map((model) => (
                              <option key={model.model} value={model.model}>
                                {model.name}
                              </option>
                            ))}
                            {clonedVoices.map((voice) => (
                              <option key={voice.voice_id} value={`${CLONED_VOICE_PREFIX}${voice.voice_id}`}>
                                Cloned: {voice.name}
                              </option>
                            ))}
                            <option value={CLONE_VOICE_UPLOAD_VALUE}>Clone Voice Upload</option>
                          </select>
                          {staffUser.voicemail_voice === CLONE_VOICE_UPLOAD_VALUE && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                              <input type="text" className="border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Voice name" value={cloneVoiceForms[staffUser.id]?.name || ""} onChange={(e) => handleCloneVoiceFormChange(staffUser.id, { name: e.target.value })} />
                              <input type="file" accept=".mp3,.wav,audio/mpeg,audio/wav,audio/wave,audio/x-wav" className="border border-gray-600 rounded-lg px-3 py-2 text-sm bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" onChange={(e) => handleCloneVoiceFormChange(staffUser.id, { file: e.target.files?.[0] || null })} />
                              <button type="button" onClick={() => handleUploadCloneVoice(staffUser.id)} disabled={cloneVoiceUploading === staffUser.id} className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${cloneVoiceUploading === staffUser.id ? "bg-gray-500 cursor-not-allowed text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}>
                                {cloneVoiceUploading === staffUser.id ? "Uploading..." : "Upload"}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <NoDataFound />
            )}

            <div className="flex justify-end gap-2 pt-4">
              <button onClick={() => setStaffVoiceModalOpen(false)} className="px-4 py-2 text-sm rounded-md border border-gray-600 hover:bg-gray-700 transition-colors text-gray-300">
                Cancel
              </button>
              <button onClick={handleSaveStaffVoices} disabled={staffVoiceSaving || staffVoiceLoading || cloneVoiceUploading !== null} className={`px-4 py-2 text-sm rounded-md text-white ${staffVoiceSaving || staffVoiceLoading || cloneVoiceUploading !== null ? "bg-gray-500 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"} transition-colors`}>
                {staffVoiceSaving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ----- Detail Modal (View) ----- */}
      {detailModalOpen && selectedVoicemail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-100">Voicemail Details</h2>
              <button onClick={() => setDetailModalOpen(false)} className="text-gray-400 hover:text-gray-200">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="space-y-2 text-gray-300">
              <p>
                <strong>ID:</strong> {selectedVoicemail.id}
              </p>
              <p>
                <strong>Type:</strong> {getVoicemailTypeLabel(selectedVoicemail.voicemail_type)}
              </p>
              <p>
                <strong>Content:</strong> {selectedVoicemail.content}
              </p>
              {selectedVoicemail.ghl_field_key && (
                <p>
                  <strong>GHL Field:</strong> {selectedVoicemail.ghl_field_key}
                </p>
              )}
              <p>
                <strong>Background Sound:</strong> {getBackgroundSoundName(selectedVoicemail.bg_url || selectedVoicemail.background_sound_url)}
              </p>
              <p>
                <strong>File Name:</strong> {selectedVoicemail.file_name || "—"}
              </p>
              <p>
                <strong>Created At:</strong> {formatDate(selectedVoicemail.created_at)}
              </p>
              <div>
                <strong>Audio:</strong>
                {selectedVoicemail.audio_url ? <audio controls src={selectedVoicemail.audio_url} className="w-full mt-2" /> : " N/A"}
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setDetailModalOpen(false)} className="px-4 py-2 text-sm rounded-md border border-gray-600 hover:bg-gray-700 transition-colors text-gray-300">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ----- Delete Confirmation ----- */}
      {selectedVoicemailId && (
        <DeleteConfirmation
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setSelectedVoicemailId(null);
          }}
          title={"Are you sure you want to delete this voicemail?"}
          id={selectedVoicemailId}
          fetchDataFromApi={() => fetchVoicemails(offset)}
          deleteMethod={handleDelete}
        />
      )}
    </div>
  );
}
